import React, { useContext, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Link } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import Login from './Login';
import Contacts from './Contacts';
import Dashboard from './Dashboard';
import ManageStatuses from './ManageStatuses';
import Header from './Header';
import PlotMap from './PlotMap';
import SalesConfig from './SalesConfig';
import ProductConfig from './ProductConfig';
import DataManagement from './DataManagement';
import UserManagement from './UserManagement';
import UserSettings from './UserSettings';
import SiteSettings from './SiteSettings';
import PublicPortalSettings from './PublicPortalSettings';
import PublicPortal from './PublicPortal';
import HelpPage from './HelpPage';
import FloatingHelpButton from './FloatingHelpButton';
import Analytics from './Analytics';
import GeospatialImport from './GeospatialImport';
import WorkOrders from './WorkOrders';
import WorkOrderPublicUpdate from './WorkOrderPublicUpdate';
import FlexFieldsConfig from './FlexFieldsConfig';
import Notifications from './Notifications';
import FeaturesPage from './FeaturesPage';
import StripePaymentPage from './StripePaymentPage';
import Obituaries from './Obituaries';
import ObituaryForm from './ObituaryForm';
import DocumentTemplates from './DocumentTemplates';
import DocumentTemplateEditor from './DocumentTemplateEditor';
import TenantManagement from './TenantManagement';
import SSOHandler from './SSOHandler';
import SiteDeactivated from './SiteDeactivated';

// Protected route wrapper for super_admin-only routes
const SuperAdminRoute = ({ children, user, loading }) => {
  if (loading) return null;
  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Protected route wrapper for admin-only routes
const AdminRoute = ({ children, user, loading }) => {
  // Wait for user data to load before making redirect decision
  if (loading) {
    return null; // Or could return a loading spinner
  }
  // If user is not admin (or super_admin), redirect to dashboard
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Protected route wrapper that blocks grounds_crew from non-work-order routes
const StandardRoute = ({ children, user, loading }) => {
  if (loading) {
    return null;
  }
  if (user?.role === 'grounds_crew') {
    return <Navigate to="/work-orders" replace />;
  }
  return children;
};

// Protected route wrapper that checks plan-based feature access
const FeatureRoute = ({ children, feature, hasFeature, loading }) => {
  if (loading) return null;
  if (!hasFeature(feature)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Inner component that has access to router hooks
function AppRouterInner() {
  const appContext = useContext(AppContext);
  const { token, user, loading, hasFeature, tenantStatus, siteName } = appContext;
  const location = useLocation();
  const [publicPortalEnabled, setPublicPortalEnabled] = useState(null);
  const [checkingPortal, setCheckingPortal] = useState(true);

  // Check if public portal is enabled for unauthenticated home page
  useEffect(() => {
    const checkPortalEnabled = async () => {
      try {
        const response = await fetch('/api/public/config');
        if (response.ok) {
          const data = await response.json();
          setPublicPortalEnabled(data.portalEnabled);
        } else {
          setPublicPortalEnabled(false);
        }
      } catch {
        setPublicPortalEnabled(false);
      } finally {
        setCheckingPortal(false);
      }
    };
    checkPortalEnabled();
  }, []);

  // Check if this is a public payment page path
  const isPaymentRoute = location.pathname.startsWith('/pay/');

  // Check if this is a public work order update path
  const isWorkOrderPublicRoute = location.pathname.startsWith('/work-orders/update/');

  // Check if current path is a public portal path (exactly /public or /public/*)
  // But NOT /public-portal-settings which is an admin route
  const isPublicRoute = location.pathname === '/public' || location.pathname.startsWith('/public/');

  // If tenant is deactivated, show deactivation screen (unless super_admin)
  // This check runs early to block access to all routes except public info
  if (tenantStatus && !tenantStatus.isActive && user?.role !== 'super_admin') {
    return <SiteDeactivated siteName={siteName} suspendReason={tenantStatus.suspendReason} />;
  }

  // For public payment routes
  if (isPaymentRoute) {
    const paymentToken = location.pathname.split('/pay/')[1];
    return <StripePaymentPage paymentToken={paymentToken} />;
  }

  // For public work order update routes
  if (isWorkOrderPublicRoute) {
    const updateToken = location.pathname.split('/work-orders/update/')[1];
    return <WorkOrderPublicUpdate updateToken={updateToken} />;
  }

  // For public routes, render without header and auth requirement
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/public/*" element={<PublicPortal showAccountLink={!!token} />} />
      </Routes>
    );
  }

  // For features marketing page (demo mode only, no auth required)
  if (location.pathname === '/features') {
    return <FeaturesPage />;
  }

  // For SSO login route (no auth required - this IS the auth)
  if (location.pathname === '/sso') {
    return <SSOHandler />;
  }

  // For the login route, always show login page
  if (location.pathname === '/login') {
    if (token) {
      return <Navigate to="/" replace />;
    }
    return <Login />;
  }

  // If not authenticated and at home page, check if we should show public portal
  if (!token && location.pathname === '/') {
    // Still checking portal status
    if (checkingPortal) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }
    // If public portal is enabled, show it at home page with login link
    if (publicPortalEnabled) {
      return <PublicPortal showLoginLink={true} />;
    }
    // If public portal is not enabled, redirect to login
    return <Navigate to="/login" replace />;
  }

  // For authenticated routes, show header and require login
  return (
    <>
      <Header />
      {!token ? (
        <Navigate to="/login" replace />
      ) : (
        <>
          <Routes>
            <Route path="/contacts" element={<StandardRoute user={user} loading={loading}><Contacts previewMode={false} /></StandardRoute>} />
            <Route path="/sales" element={<FeatureRoute feature="sales" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><SalesConfig /></StandardRoute></FeatureRoute>} />
            <Route path="/products" element={<FeatureRoute feature="products" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><ProductConfig /></StandardRoute></FeatureRoute>} />
            <Route path="/plots" element={<StandardRoute user={user} loading={loading}><PlotMap showHelp={true} showTable={true} previewMode={false} /></StandardRoute>} />
            <Route path="/statuses" element={<StandardRoute user={user} loading={loading}><ManageStatuses /></StandardRoute>} />
            <Route path="/data" element={<AdminRoute user={user} loading={loading}><DataManagement /></AdminRoute>} />
            <Route path="/settings" element={<UserSettings />} />
            <Route path="/help" element={<StandardRoute user={user} loading={loading}><HelpPage /></StandardRoute>} />
            <Route path="/analytics" element={<FeatureRoute feature="analytics" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><Analytics /></StandardRoute></FeatureRoute>} />
            <Route path="/work-orders" element={<FeatureRoute feature="workOrders" hasFeature={hasFeature} loading={loading}><WorkOrders /></FeatureRoute>} />
            <Route path="/obituaries" element={<FeatureRoute feature="obituaries" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><Obituaries /></StandardRoute></FeatureRoute>} />
            <Route path="/obituaries/new" element={<FeatureRoute feature="obituaries" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><ObituaryForm /></StandardRoute></FeatureRoute>} />
            <Route path="/obituaries/:id" element={<FeatureRoute feature="obituaries" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><ObituaryForm /></StandardRoute></FeatureRoute>} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/documents/templates" element={<FeatureRoute feature="documents" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><DocumentTemplates /></StandardRoute></FeatureRoute>} />
            <Route path="/documents/templates/new" element={<FeatureRoute feature="documents" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><DocumentTemplateEditor /></StandardRoute></FeatureRoute>} />
            <Route path="/documents/templates/:id" element={<FeatureRoute feature="documents" hasFeature={hasFeature} loading={loading}><StandardRoute user={user} loading={loading}><DocumentTemplateEditor /></StandardRoute></FeatureRoute>} />
            {/* Admin-only routes - protected with AdminRoute wrapper */}
            <Route
              path="/users"
              element={
                <AdminRoute user={user} loading={loading}>
                  <UserManagement />
                </AdminRoute>
              }
            />
            <Route
              path="/site-settings"
              element={
                <AdminRoute user={user} loading={loading}>
                  <SiteSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/public-portal-settings"
              element={
                <AdminRoute user={user} loading={loading}>
                  <PublicPortalSettings />
                </AdminRoute>
              }
            />
            <Route
              path="/flex-fields"
              element={
                <FeatureRoute feature="flexFields" hasFeature={hasFeature} loading={loading}>
                  <AdminRoute user={user} loading={loading}>
                    <FlexFieldsConfig />
                  </AdminRoute>
                </FeatureRoute>
              }
            />
            <Route
              path="/geospatial-import"
              element={
                <AdminRoute user={user} loading={loading}>
                  <GeospatialImport />
                </AdminRoute>
              }
            />
            {/* Super admin-only routes */}
            <Route
              path="/tenants"
              element={
                <SuperAdminRoute user={user} loading={loading}>
                  <TenantManagement />
                </SuperAdminRoute>
              }
            />
            <Route path="/" element={user?.role === 'grounds_crew' && hasFeature('workOrders') ? <Navigate to="/work-orders" replace /> : <Dashboard />} />
            {/* Catch-all route for unknown paths */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <FloatingHelpButton />
        </>
      )}
    </>
  );
}

function AppRouter() {
  return (
    <Router>
      <AppRouterInner />
    </Router>
  );
}

export default AppRouter;
